import { FC, useState } from 'react';
import { Formik } from 'formik';
import { Form } from 'react-router-dom';
import { Box, Checkbox, FormControl, FormControlLabel, FormGroup, Typography } from '@mui/material';
import InformationIcon from '../../../../../../../assets/icons/Info, Infornation.png';
import { useAppDispatch, useAppSelector } from '../../../../../../../hooks/redux';
import {
  useGetActivityInterestQuery,
  useUpdateUserAdditionalInfoMutation,
} from '../../../../../../../store/services/customizeOptionsApi';

import CustomButton from '../../../../../../../common/CustomButton';

import styles from './PrimaryTravelPurpose.module.scss';
import { setUserInfo } from '../../../../../../../store/slices/userSlice';

type FormikValues = {
  travelPurpose?: string[];
};

type PrimaryTravelPurposeProps = {
  closeModal: () => void;
};

const PrimaryTravelPurpose: FC<PrimaryTravelPurposeProps> = ({ closeModal }) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.user);
  const [updateUserAdditionalInfo, { isSuccess }] = useUpdateUserAdditionalInfoMutation();
  const { data: primaryTravelPurpose } = useGetActivityInterestQuery('primary_travel_purposes');
  const { type: planType } = useAppSelector(state => state.plan);

  const [resErrors, setResErrors] = useState<{ attr: string | null; code: string; detail: string }[] | null>(null);

  const initialValues = {
    travelPurpose: user.primary_travel_purposes,
  };

  const handleSubmit = async (values: FormikValues) => {
    const payload = {
      primary_travel_purposes: values.travelPurpose,
    };

    try {
      await updateUserAdditionalInfo(payload);
      dispatch(setUserInfo(payload));
      setResErrors(null);
      closeModal();
    } catch (error) {
      console.error('Error updating user info:', error);
      setResErrors((error as any)?.data?.errors || null);
    }
  };

  const formikValidate = (values: FormikValues) => {
    const errors: FormikValues = {};

    return errors;
  };

  const availableOptionsCount = 6;
  return (
    <Box className={styles.primaryTravel}>
      <Formik
        initialValues={initialValues}
        validate={formikValidate}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, handleSubmit, setFieldValue, isSubmitting }) => (
          <Form
            className={styles.primaryTravelForm}
            onSubmit={handleSubmit}
          >
            <FormControl
              component="fieldset"
              className={styles.radioGroupWrapper}
            >
              <Typography
                variant="h5Bold"
                className={styles.stepTitle}
              >
                Let's personalize your AI assistant
              </Typography>
              <Typography
                variant="h10"
                className={styles.stepSubtitle}
              >
                Primary Travel Purpose (Select Upto {availableOptionsCount})
              </Typography>
              <FormGroup className={styles.checkboxesGroup}>
                {primaryTravelPurpose?.map((option: { name: string; is_free: boolean }) => {
                  const isChecked = values.travelPurpose?.includes(option.name);
                  const reachMaxLimit =
                    values.travelPurpose && values.travelPurpose.length >= availableOptionsCount && !isChecked;
                  const isDisabled = (planType.includes('Basic') || planType === '') && !option.is_free;
                  const finalDisabled = isDisabled || reachMaxLimit;

                  return (
                    <FormControlLabel
                      key={option.name}
                      className={`${styles.checkLabel} ${isChecked ? styles.checked : ''} ${finalDisabled ? styles.disabledOption : ''}`}
                      control={
                        <Checkbox
                          className={styles.checkbox}
                          checked={isChecked}
                          disabled={finalDisabled}
                          onChange={event => {
                            if (event.target.checked) {
                              setFieldValue(
                                'travelPurpose',
                                values.travelPurpose && !!values.travelPurpose[0]?.length
                                  ? [...values.travelPurpose, option.name]
                                  : [option.name]
                              );
                            } else {
                              setFieldValue(
                                'travelPurpose',
                                values.travelPurpose?.filter((item: string) => item !== option.name)
                              );
                            }
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant={isChecked ? 'h10Semibold' : 'h10'}
                          className={finalDisabled ? styles.disabledOptionText : ''}
                        >
                          {option.name}
                        </Typography>
                      }
                    />
                  );
                })}
              </FormGroup>
            </FormControl>

            <Box className={styles.additionalInfo}>
              <Typography
                variant="h11"
                className={styles.additionalInfoText}
              >
                To get badges, fill in all the questions
              </Typography>
              <img
                src={InformationIcon}
                className={styles.additionalInfoImg}
                alt="info"
              />
            </Box>

            <Box className={styles.cityAndAiroportBtns}>
              <CustomButton
                btnType="button"
                variant="outlined"
                onClick={closeModal}
              >
                Cancel
              </CustomButton>
              <CustomButton
                btnType="submit"
                customClass={styles.customButton}
                disabled={isSubmitting}
              >
                Save
              </CustomButton>
            </Box>
            {isSuccess && (
              <Typography
                variant="h12"
                sx={{ color: '#208B64' }}
              >
                Primary Travel Purpose updated successfully.
              </Typography>
            )}
            {resErrors &&
              resErrors.map((error, index) => (
                <Typography
                  key={index}
                  variant="h12"
                  sx={{ color: '#C8102E' }}
                >
                  {error.detail}
                </Typography>
              ))}
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default PrimaryTravelPurpose;
